import i18n from 'i18next';
import axios from '../../../shared/utils/axios';
import { serializeQuerystring } from '../../../shared/utils/object';
import { addToast } from '../toasts/toastsActions';
import { toastTypes } from '../../../shared/components/Toast/ToastOptions';
import { actionSuccess } from '../utils';
import { withoutGeneralSalaryAndSalaryType } from './helpers';

export const FETCH_EMPLOYEES = 'FETCH_EMPLOYEES';
export const PUT_EMPLOYEE = 'PUT_EMPLOYEE';
export const POST_EMPLOYEE = 'POST_EMPLOYEE';
export const UNLOCK_EMPLOYEE = 'UNLOCK_EMPLOYEE';
export const RESEND_ACTIVATION_EMPLOYEE = 'RESEND_ACTIVATION_EMPLOYEE';
export const PUT_EMPLOYEE_STATUS = 'PUT_EMPLOYEE_STATUS';
export const START_LOADING_EMPLOYEES = 'START_LOADING_EMPLOYEES';
export const END_LOADING_EMPLOYEES = 'END_LOADING_EMPLOYEES';

const employeesEndpoint = `${process.env.REACT_APP_API_URL}employees`;
const fetchEmployeesEndpoint = (filters = {}) => {
  let filterString = serializeQuerystring({
    searchTerm: filters.searchTerm || ''
  });
  if (filters.currentFilters) filterString += filters.currentFilters;
  return `${process.env.REACT_APP_API_URL}employees?offset=${filters?.offset || 0}&limit=${
    filters?.rowsPerPage || 15
  }${filterString}`;
};
const employeeEndpoint = personalId => `${process.env.REACT_APP_API_URL}employee/${personalId}`;
const unlockEmployeeEndpoint = personalId => `${process.env.REACT_APP_API_URL}employee/${personalId}/unlock`;
const resendActivationEndpoint = personalId =>
  `${process.env.REACT_APP_API_URL}employee/${personalId}/reset-activation`;
const dummifyEmployeeEndpoint = personalId =>
  `${process.env.REACT_APP_API_URL}super/employee/${personalId}/dummify-employee`;

export const startLoadingEmployees = () => ({
  type: START_LOADING_EMPLOYEES
});

export const endLoadingEmployees = () => ({
  type: END_LOADING_EMPLOYEES
});

export const fetchEmployees =
  (filters = {}) =>
  async dispatch => {
    try {
      dispatch(startLoadingEmployees());
      const response = await axios.get(fetchEmployeesEndpoint(filters));
      dispatch(actionSuccess(FETCH_EMPLOYEES, response?.data));

      return response?.data?.data;
    } catch (error) {
      dispatch(addToast(toastTypes.error, error));
    } finally {
      dispatch(endLoadingEmployees());
    }
  };

export const putEmployee = (personalId, employee, successCallback, failureCallback) => async dispatch => {
  try {
    const employeeObjFiltered = withoutGeneralSalaryAndSalaryType(employee);

    const result = await axios.put(employeeEndpoint(personalId), employeeObjFiltered);
    dispatch(actionSuccess(PUT_EMPLOYEE, employee));
    dispatch(
      addToast({
        ...toastTypes.success,
        defaultMessage: i18n.t('employees.put.success', { ns: ['actions'] })
      })
    );
    successCallback(result.data);
    return result.data;
  } catch (error) {
    const responseStatus = error.response?.status;

    dispatch(
      addToast(
        {
          ...toastTypes.error,
          defaultMessage: i18n.t([`employees.put.error.${responseStatus}`, 'employees.put.error.unspecific'], {
            ns: ['actions']
          })
        },
        error
      )
    );
    failureCallback();
  }
};

export const putEmployeeStatus =
  (employee, status, withoutToast = false) =>
  async dispatch => {
    try {
      const result = await axios.put(employeeEndpoint(employee.personalId), status);

      if (withoutToast) {
        return result.data;
      }

      dispatch(
        actionSuccess(PUT_EMPLOYEE_STATUS, {
          employee,
          status
        })
      );

      dispatch(
        addToast({
          ...toastTypes.success,
          defaultMessage: i18n.t('employees.update.success', {
            ns: ['actions']
          })
        })
      );
      return result.data;
    } catch (error) {
      if (withoutToast) {
        return;
      }

      dispatch(
        addToast(
          {
            ...toastTypes.error,
            defaultMessage: i18n.t('employees.update.error', {
              ns: ['actions']
            })
          },
          error
        )
      );
    }
  };

export const postEmployee = (employee, successCallback, failureCallback) => async dispatch => {
  try {
    const employeeObjFiltered = withoutGeneralSalaryAndSalaryType(employee);

    await axios.post(employeesEndpoint, employeeObjFiltered);
    dispatch(actionSuccess(POST_EMPLOYEE, employeeObjFiltered));

    dispatch(
      addToast({
        ...toastTypes.success,
        defaultMessage: i18n.t('employees.put.success', { ns: ['actions'] })
      })
    );
    successCallback();
  } catch (error) {
    const responseStatus = error.response?.status;

    dispatch(
      addToast(
        {
          ...toastTypes.error,
          defaultMessage: i18n.t([`employees.put.error.${responseStatus}`, 'employees.put.error.unspecific'], {
            ns: ['actions']
          })
        },
        error
      )
    );
    if (failureCallback) failureCallback();
  }
};

export const unlockEmployee = employee => async dispatch => {
  try {
    await axios.put(unlockEmployeeEndpoint(employee.personalId));
    dispatch(actionSuccess(UNLOCK_EMPLOYEE, employee));
    dispatch(
      addToast({
        ...toastTypes.success,
        defaultMessage: i18n.t('employees.update.success', { ns: ['actions'] })
      })
    );
  } catch (error) {
    dispatch(
      addToast(
        {
          ...toastTypes.error,
          defaultMessage: i18n.t('employees.update.error', { ns: ['actions'] })
        },
        error
      )
    );
  }
};

export const resendActivation = employee => async dispatch => {
  try {
    await axios.post(resendActivationEndpoint(employee.personalId));
    dispatch(actionSuccess(RESEND_ACTIVATION_EMPLOYEE, employee));

    dispatch(
      addToast({
        ...toastTypes.success,
        defaultMessage: i18n.t('employees.update.success', { ns: ['actions'] })
      })
    );
  } catch (error) {
    dispatch(
      addToast(
        {
          ...toastTypes.error,
          defaultMessage: i18n.t('employees.update.error', { ns: ['actions'] })
        },
        error
      )
    );
  }
};

export const dummifyEmployee = personalId => async dispatch => {
  try {
    const response = await axios.put(dummifyEmployeeEndpoint(personalId));

    dispatch(
      addToast({
        ...toastTypes.success,
        defaultMessage: `${i18n.t('employees.dummified.success', {
          ns: ['actions']
        })} ${response?.data?.message}`
      })
    );
    return response?.data?.data;
  } catch (error) {
    const message = i18n.t('employees.dummified.error', {
      ns: ['actions']
    });

    dispatch(
      addToast(
        {
          ...toastTypes.error,
          defaultMessage: message
        },
        error
      )
    );
  }
};
